/**
 * Pure helpers for the customer-facing CompareSources dialog. Extracted so the
 * classification/filtering/permalink logic can be unit-tested without pulling
 * in Apollo or MUI.
 *
 * Standalone by design — M3 must not import the M2-only `manifestSourceDisplay`
 * / `manifestFieldNames` modules, so the source labels live here. Dot colors
 * come from the zero-dependency `sourceColors` leaf (shared with M2, no logic).
 */
import { WasteManifestSource } from "generated-graphql/graphql";

import { SOURCE_DOT_HEX } from "./sourceColors";

/**
 * Group label the resolver assigns to manifest-level rows (mirrors
 * `MANIFEST_GROUP` in compareManifestSources.ts). The UI renders this group
 * un-headered so the manifest fields read as the table's top line.
 */
export const MANIFEST_GROUP_LABEL = "Manifest";

/** The three source pillars, in display priority order. */
export const COMPARE_SOURCE_ORDER: readonly WasteManifestSource[] = [
  WasteManifestSource.EManifest,
  WasteManifestSource.Vendor,
  WasteManifestSource.ManifestDocument,
];

/** Short human label for each source pillar. */
export function getSourceLabel(source: WasteManifestSource): string {
  switch (source) {
    case WasteManifestSource.EManifest:
      return "e-Manifest";
    case WasteManifestSource.Vendor:
      return "Vendor";
    case WasteManifestSource.ManifestDocument:
      return "Document";
  }
}

/**
 * Color key used to tint a source's column dot — green / blue / indigo for
 * e-Manifest / Vendor / Document. Standalone (M3 stays independently testable).
 */
export type SourceColorKey = "success" | "info" | "document";

/**
 * Bright/neon source-dot colors, one per {@link SourceColorKey} — the neon accent
 * of each hue so the small dots are easy to tell apart. Values come from the
 * shared {@link SOURCE_DOT_HEX} (same source as M2's dots, so they can't drift).
 * Document is indigo, NOT orange (orange = staff-only).
 */
export const SOURCE_DOT_COLOR: Record<SourceColorKey, string> = {
  success: SOURCE_DOT_HEX.eManifest,
  info: SOURCE_DOT_HEX.vendor,
  document: SOURCE_DOT_HEX.document,
};

export function getSourceColorKey(source: WasteManifestSource): SourceColorKey {
  switch (source) {
    case WasteManifestSource.EManifest:
      return "success";
    case WasteManifestSource.Vendor:
      return "info";
    case WasteManifestSource.ManifestDocument:
      return "document";
  }
}

/** The minimal compare-row shape the helpers operate on. */
export interface CompareRowLike {
  key: string;
  group: string;
  field: string;
  differs: boolean;
  missingInSomeSource: boolean;
}

/**
 * Filter rows for the "show only differences" toggle. When enabled, only rows
 * that `differs` are kept; otherwise all rows pass through unchanged.
 */
export function filterRowsByDifferences<T extends CompareRowLike>(
  rows: readonly T[],
  showOnlyDifferences: boolean
): T[] {
  if (!showOnlyDifferences) return [...rows];
  return rows.filter((row) => row.differs);
}

/**
 * Footer counts computed client-side as a fallback / sanity check. The server
 * also returns these, but recomputing keeps the chips correct if a future
 * caller passes pre-filtered rows.
 */
export function computeCompareFooterCounts(rows: readonly CompareRowLike[]): {
  differenceCount: number;
  missingCount: number;
} {
  let differenceCount = 0;
  let missingCount = 0;
  for (const row of rows) {
    if (row.differs) differenceCount += 1;
    if (row.missingInSomeSource) missingCount += 1;
  }
  return { differenceCount, missingCount };
}

/**
 * Given each source's value for one field, return the set of sources whose
 * value is an "outlier" — i.e. it diverges from the consensus among the
 * sources that actually have a value. The semantics:
 *
 * - 2 sources agree + 1 differs → only the lone source is an outlier.
 * - Every populated value distinct, or a tie for the most-common value (no
 *   clear majority) → every populated source is an outlier.
 * - Unanimous, or fewer than two populated values → no outliers.
 *
 * Absent (null) values never count as outliers — "missing" is rendered
 * separately.
 */
export function computeOutlierSources<S>(
  entries: readonly { source: S; value: string | null }[]
): Set<S> {
  const result = new Set<S>();
  const present = entries.filter((e): e is { source: S; value: string } => e.value != null);
  if (present.length < 2) return result;

  const counts = new Map<string, number>();
  for (const entry of present) {
    counts.set(entry.value, (counts.get(entry.value) ?? 0) + 1);
  }
  if (counts.size < 2) return result; // unanimous — nothing diverges

  const maxCount = Math.max(...counts.values());
  const sourcesAtMax = [...counts.values()].filter((count) => count === maxCount).length;
  const hasClearMajority = sourcesAtMax === 1;

  for (const entry of present) {
    const count = counts.get(entry.value) ?? 0;
    if (!hasClearMajority || count < maxCount) result.add(entry.source);
  }
  return result;
}

/**
 * Group rows by their `group` label, preserving first-seen order. Used to
 * render section-header rows in the table.
 */
export function groupCompareRows<T extends CompareRowLike>(
  rows: readonly T[]
): { group: string; rows: T[] }[] {
  const order: string[] = [];
  const map = new Map<string, T[]>();
  for (const row of rows) {
    const existing = map.get(row.group);
    if (existing) {
      existing.push(row);
    } else {
      order.push(row.group);
      map.set(row.group, [row]);
    }
  }
  return order.map((group) => ({ group, rows: map.get(group) ?? [] }));
}

// --- ?compare= permalink ---

/**
 * Parsed form of the `?compare=` URL parameter.
 *
 * - `{ mode: "open" }` — `compare=open`: just open the dialog.
 * - `{ mode: "line", wasteLineUnifiedId }` — `compare=line:<wluId>`: open
 *   scoped to a single line item.
 * - `{ mode: "field", field }` — `compare=field:<fieldName>`: open and
 *   highlight/scroll to that field row.
 */
export type CompareParam =
  | { mode: "open" }
  | { mode: "line"; wasteLineUnifiedId: string }
  | { mode: "field"; field: string };

/**
 * Parse the raw `?compare=` value. Returns null when absent or malformed so the
 * dialog stays closed. `compare=open` (and any unrecognized bare value treated
 * as open) opens the full-manifest view.
 */
export function parseCompareParam(raw: string | null | undefined): CompareParam | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;

  if (trimmed === "open") return { mode: "open" };

  const separatorIndex = trimmed.indexOf(":");
  if (separatorIndex === -1) {
    // Bare non-"open" token — treat as open for forward-compatibility.
    return { mode: "open" };
  }

  const prefix = trimmed.slice(0, separatorIndex);
  const value = trimmed.slice(separatorIndex + 1).trim();
  if (value.length === 0) return null;

  if (prefix === "line") return { mode: "line", wasteLineUnifiedId: value };
  if (prefix === "field") return { mode: "field", field: value };

  // Unknown prefix — fall back to opening the dialog rather than swallowing it.
  return { mode: "open" };
}

/** Encode a {@link CompareParam} back into its `?compare=` string value. */
export function encodeCompareParam(param: CompareParam): string {
  switch (param.mode) {
    case "open":
      return "open";
    case "line":
      return `line:${param.wasteLineUnifiedId}`;
    case "field":
      return `field:${param.field}`;
  }
}
